import { useEffect, useRef, useState } from 'react';

const WALK_FRAME_COUNT = 6;
const DOG_ANIMATIONS = {
  idle: { start: 1, frames: 6, fps: 5, moving: false },
  sit: { start: 8, frames: 6, fps: 4, moving: false },
  lie: { start: 14, frames: 6, fps: 4, moving: false },
  run: { start: 20, frames: 8, fps: 9, moving: true },
  walk: { start: 28, frames: 8, fps: 7, moving: true },
  beg: { start: 36, frames: 6, fps: 4, moving: false },
  sleep: { start: 42, frames: 4, fps: 2, moving: false },
};
const DOG_IDLE_ACTIONS = ['idle', 'idle', 'sit', 'sit', 'lie', 'beg', 'sleep'];

function GroundWalker() {
  const walkerRef = useRef(null);
  const directionRef = useRef('east');
  const movingRef = useRef(true);
  const [frame, setFrame] = useState(0);
  const [motion, setMotion] = useState({ direction: 'east', moving: true });

  useEffect(() => {
    const walker = walkerRef.current;

    if (!walker) {
      return undefined;
    }

    let animationFrame;
    let decisionTimer;
    let lastTime;
    let x = 0;

    const randomBetween = (minimum, maximum) => (
      minimum + Math.random() * (maximum - minimum)
    );

    const getLane = () => {
      const isMobile = window.innerWidth <= 640;
      const characterWidth = isMobile ? 60 : 72;
      const defaultStart = isMobile
        ? 16
        : Math.min(410, Math.max(330, window.innerWidth * 0.3));
      const pondRight = document
        .querySelector('.home-ground__terrain-pond')
        ?.getBoundingClientRect().right;
      const pondGap = isMobile ? 12 : 24;
      const requestedStart = pondRight
        ? Math.max(defaultStart, pondRight + pondGap)
        : defaultStart;
      const cabinLeft = document
        .querySelector('.home-ground__winter-cabin')
        ?.getBoundingClientRect().left;
      const screenEdge = window.innerWidth - characterWidth - 16;
      const cabinGap = isMobile ? 12 : 28;
      const end = cabinLeft
        ? Math.min(screenEdge, cabinLeft - characterWidth - cabinGap)
        : screenEdge;
      const safeEnd = Math.max(0, end);

      return {
        start: Math.min(requestedStart, safeEnd),
        end: safeEnd,
        speed: isMobile ? 16 : 22,
      };
    };

    const setCharacterMotion = (direction, moving) => {
      directionRef.current = direction;
      movingRef.current = moving;
      setMotion({ direction, moving });
      setFrame(0);
    };

    const scheduleDecision = (minimum = 2800, maximum = 6200) => {
      window.clearTimeout(decisionTimer);
      decisionTimer = window.setTimeout(makeDecision, randomBetween(minimum, maximum));
    };

    const makeDecision = () => {
      const roll = Math.random();

      if (!movingRef.current) {
        if (roll < 0.32) {
          setCharacterMotion(Math.random() < 0.5 ? 'north' : 'south', false);
          scheduleDecision(1400, 3400);
          return;
        }

        setCharacterMotion(Math.random() < 0.5 ? 'east' : 'west', true);
        scheduleDecision(3200, 7200);
        return;
      }

      if (roll < 0.6) {
        setCharacterMotion(Math.random() < 0.5 ? 'north' : 'south', false);
        scheduleDecision(1600, 4200);
      } else {
        scheduleDecision(3000, 7000);
      }
    };

    const positionWalker = () => {
      const lane = getLane();
      x = Math.min(lane.end, Math.max(lane.start, x || lane.start));
      walker.style.transform = `translate3d(${x}px, 0, 0)`;
    };

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (prefersReducedMotion) {
      x = getLane().start;
      setCharacterMotion('south', false);
      positionWalker();
      window.addEventListener('resize', positionWalker);

      return () => window.removeEventListener('resize', positionWalker);
    }

    const walk = (time) => {
      const lane = getLane();

      if (lastTime === undefined) {
        lastTime = time;
        x = lane.start;
      }

      const elapsed = Math.min(50, time - lastTime) / 1000;
      lastTime = time;
      x = Math.min(lane.end, Math.max(lane.start, x));

      if (movingRef.current) {
        x += (directionRef.current === 'east' ? 1 : -1) * lane.speed * elapsed;

        if (x >= lane.end) {
          x = lane.end;
          setCharacterMotion(Math.random() < 0.5 ? 'north' : 'south', false);
          scheduleDecision(1400, 3200);
        } else if (x <= lane.start) {
          x = lane.start;
          setCharacterMotion(Math.random() < 0.5 ? 'north' : 'south', false);
          scheduleDecision(1400, 3200);
        }
      }

      walker.style.transform = `translate3d(${x}px, 0, 0)`;
      animationFrame = window.requestAnimationFrame(walk);
    };

    animationFrame = window.requestAnimationFrame(walk);
    scheduleDecision();
    window.addEventListener('resize', positionWalker);

    return () => {
      window.cancelAnimationFrame(animationFrame);
      window.clearTimeout(decisionTimer);
      window.removeEventListener('resize', positionWalker);
    };
  }, []);

  useEffect(() => {
    if (!motion.moving) {
      setFrame(0);
      return undefined;
    }

    const frameTimer = window.setInterval(() => {
      setFrame((currentFrame) => (currentFrame + 1) % WALK_FRAME_COUNT);
    }, 180);

    return () => window.clearInterval(frameTimer);
  }, [motion.moving]);

  const frameName = `frame_${String(frame).padStart(3, '0')}.png`;
  const spriteSource = motion.moving
    ? `/images/casino-character/walking/${motion.direction}/${frameName}`
    : `/images/casino-character/idle/${motion.direction}.png`;

  return (
    <div
      ref={walkerRef}
      className={`home-ground__walker${motion.moving ? ' is-walking' : ' is-idle'}`}
      data-direction={motion.direction}
    >
      <img
        className="home-ground__walker-sprite"
        src={spriteSource}
        alt=""
      />
    </div>
  );
}

function GroundDog() {
  const dogRef = useRef(null);
  const actionRef = useRef('walk');
  const directionRef = useRef('west');
  const [frame, setFrame] = useState(0);
  const [behavior, setBehavior] = useState({ action: 'walk', direction: 'west' });

  useEffect(() => {
    const dog = dogRef.current;

    if (!dog) {
      return undefined;
    }

    let animationFrame;
    let decisionTimer;
    let lastTime;
    let x;

    const randomBetween = (minimum, maximum) => (
      minimum + Math.random() * (maximum - minimum)
    );

    const getLane = () => {
      const isMobile = window.innerWidth <= 640;
      const dogWidth = isMobile ? 68 : 80;
      const defaultStart = isMobile
        ? 16
        : Math.min(410, Math.max(330, window.innerWidth * 0.3));
      const pondRight = document
        .querySelector('.home-ground__terrain-pond')
        ?.getBoundingClientRect().right;
      const pondGap = isMobile ? 12 : 24;
      const requestedStart = pondRight
        ? Math.max(defaultStart, pondRight + pondGap)
        : defaultStart;
      const cabinLeft = document
        .querySelector('.home-ground__winter-cabin')
        ?.getBoundingClientRect().left;
      const screenEdge = window.innerWidth - dogWidth - 16;
      const cabinGap = isMobile ? 12 : 28;
      const end = cabinLeft
        ? Math.min(screenEdge, cabinLeft - dogWidth - cabinGap)
        : screenEdge;
      const safeEnd = Math.max(0, end);

      return {
        isMobile,
        start: Math.min(requestedStart, safeEnd),
        end: safeEnd,
        walkingSpeed: isMobile ? 15 : 20,
        runningSpeed: isMobile ? 30 : 40,
      };
    };

    const setDogBehavior = (action, direction = directionRef.current) => {
      actionRef.current = action;
      directionRef.current = direction;
      setBehavior({ action, direction });
      setFrame(0);
    };

    const scheduleDecision = (minimum, maximum) => {
      window.clearTimeout(decisionTimer);
      decisionTimer = window.setTimeout(makeDecision, randomBetween(minimum, maximum));
    };

    const makeDecision = () => {
      const roll = Math.random();

      if (window.innerWidth <= 640) {
        const action = DOG_IDLE_ACTIONS[Math.floor(Math.random() * DOG_IDLE_ACTIONS.length)];
        setDogBehavior(action, Math.random() < 0.5 ? 'east' : 'west');
        scheduleDecision(action === 'sleep' ? 4500 : 1800, action === 'sleep' ? 8000 : 5200);
        return;
      }

      if (roll < 0.22) {
        setDogBehavior('walk', Math.random() < 0.5 ? 'east' : 'west');
        scheduleDecision(3800, 8000);
      } else if (roll < 0.32) {
        setDogBehavior('run', Math.random() < 0.5 ? 'east' : 'west');
        scheduleDecision(1400, 3200);
      } else {
        const action = DOG_IDLE_ACTIONS[Math.floor(Math.random() * DOG_IDLE_ACTIONS.length)];
        setDogBehavior(action);
        scheduleDecision(action === 'sleep' ? 4500 : 1800, action === 'sleep' ? 8000 : 5200);
      }
    };

    const positionDog = () => {
      const lane = getLane();
      x = lane.isMobile
        ? lane.end
        : Math.min(lane.end, Math.max(lane.start, x ?? lane.end - 80));
      dog.style.transform = `translate3d(${x}px, 0, 0)`;
    };

    const handleResize = () => {
      if (window.innerWidth <= 640 && DOG_ANIMATIONS[actionRef.current].moving) {
        setDogBehavior('idle', directionRef.current);
      }

      positionDog();
    };

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (prefersReducedMotion) {
      x = getLane().end;
      setDogBehavior('sit', 'west');
      positionDog();
      window.addEventListener('resize', handleResize);

      return () => window.removeEventListener('resize', handleResize);
    }

    if (window.innerWidth <= 640) {
      setDogBehavior('idle', 'west');
    }

    const roam = (time) => {
      const lane = getLane();

      if (lastTime === undefined) {
        lastTime = time;
        x = lane.isMobile ? lane.end : Math.max(lane.start, lane.end - 80);
      }

      const elapsed = Math.min(50, time - lastTime) / 1000;
      lastTime = time;
      x = Math.min(lane.end, Math.max(lane.start, x));
      const animation = DOG_ANIMATIONS[actionRef.current];

      if (animation.moving) {
        const speed = actionRef.current === 'run' ? lane.runningSpeed : lane.walkingSpeed;
        x += (directionRef.current === 'east' ? 1 : -1) * speed * elapsed;

        if (x >= lane.end) {
          x = lane.end;
          setDogBehavior(actionRef.current, 'west');
        } else if (x <= lane.start) {
          x = lane.start;
          setDogBehavior(actionRef.current, 'east');
        }
      }

      dog.style.transform = `translate3d(${x}px, 0, 0)`;
      animationFrame = window.requestAnimationFrame(roam);
    };

    animationFrame = window.requestAnimationFrame(roam);
    scheduleDecision(2200, 5200);
    window.addEventListener('resize', handleResize);

    return () => {
      window.cancelAnimationFrame(animationFrame);
      window.clearTimeout(decisionTimer);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    const animation = DOG_ANIMATIONS[behavior.action];

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      setFrame(0);
      return undefined;
    }

    const frameTimer = window.setInterval(() => {
      setFrame((currentFrame) => (currentFrame + 1) % animation.frames);
    }, 1000 / animation.fps);

    return () => window.clearInterval(frameTimer);
  }, [behavior.action]);

  const animation = DOG_ANIMATIONS[behavior.action];
  const sourceFrame = animation.start + frame;
  const frameName = `auto-${String(sourceFrame).padStart(3, '0')}.png`;
  const spriteSource = `/images/dog/theodore/${behavior.direction}/${frameName}`;

  return (
    <div
      ref={dogRef}
      className={`home-ground__dog is-${behavior.action}`}
      data-direction={behavior.direction}
    >
      <span className="home-ground__dog-name">Theodore</span>
      <img className="home-ground__dog-sprite" src={spriteSource} alt="" />
    </div>
  );
}

export default function AnimatedBackground() {
  return (
    <>
      <div className="stars"></div>
      <div className="home-sky-scene" aria-hidden="true">
        <img
          className="home-sky-scene__image"
          src="/images/backgrounds/mountains-static.png"
          alt=""
        />
      </div>
      <div className="home-aurora-layer" aria-hidden="true">
        <img
          className="home-aurora-overlay"
          src="/images/aurora/single-ribbon-aurora.png"
          alt=""
        />
      </div>
      <div className="home-clouds" aria-hidden="true">
        <img
          className="home-cloud home-cloud--one"
          src="/images/clouds/moonlit-cloud-wide.png"
          alt=""
        />
        <img
          className="home-cloud home-cloud--two"
          src="/images/clouds/moonlit-cloud-small.png"
          alt=""
        />
        <img
          className="home-cloud home-cloud--three"
          src="/images/clouds/moonlit-cloud-wide.png"
          alt=""
        />
        <img
          className="home-cloud home-cloud--four"
          src="/images/clouds/moonlit-cloud-small.png"
          alt=""
        />
      </div>
      <div className="home-ground" aria-hidden="true">
        <div
          className="home-ground__snow-cap"
          style={{ backgroundImage: "url('/images/ground/snowy-ground.png')" }}
        ></div>
        <img
          className="home-ground__terrain-pond"
          src="/images/ground/terrain-pond-v2.png"
          alt=""
        />
        <img
          className="home-ground__winter-cabin"
          src="/images/ground/winter-cabin.png"
          alt=""
        />
        <GroundWalker />
        <GroundDog />
      </div>
    </>
  );
}
